// The kit's drawings: inline SVG, filled paths on a 24 grid, colour from currentColor.
// No icon library. docs/operations/design-standard.md, "Controls".

import React from 'react';

export interface IconProps { className?: string }

const Svg: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <svg aria-hidden="true" focusable="false" className={className} viewBox="0 0 24 24" fill="none">
    {children}
  </svg>
);

export const IconPlay: React.FC<IconProps> = ({ className = 'h-2.5 w-2.5' }) => (
  <Svg className={className}>
    <path d="M7 4.5v15a1 1 0 001.5.86l12-7.5a1 1 0 000-1.72l-12-7.5A1 1 0 007 4.5z" fill="currentColor" />
  </Svg>
);

export const IconStop: React.FC<IconProps> = ({ className = 'h-2.5 w-2.5' }) => (
  <Svg className={className}>
    <rect x="5" y="5" width="14" height="14" rx="2" fill="currentColor" />
  </Svg>
);

/** Points right; `open` turns it down, for disclosure rows. */
export const IconChevron: React.FC<IconProps & { open?: boolean }> = ({ className = 'h-3.5 w-3.5', open }) => (
  <Svg className={`${className} transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}>
    <path d="M8.6 4.7a1.2 1.2 0 011.7-.1l6.6 6.5a1.2 1.2 0 010 1.8l-6.6 6.5a1.2 1.2 0 01-1.7-1.7l5.7-5.7-5.7-5.7a1.2 1.2 0 010-1.6z" fill="currentColor" />
  </Svg>
);

export const IconChevronDown: React.FC<IconProps> = ({ className = 'h-3.5 w-3.5' }) => (
  <Svg className={className}>
    <path d="M4.7 8.6a1.2 1.2 0 011.7-.1L12 14.1l5.6-5.6a1.2 1.2 0 011.7 1.7l-6.4 6.4a1.2 1.2 0 01-1.8 0L4.6 10.2a1.2 1.2 0 01.1-1.6z" fill="currentColor" />
  </Svg>
);

export const IconChevronLeft: React.FC<IconProps> = ({ className = 'h-3.5 w-3.5' }) => (
  <Svg className={className}>
    <path d="M15.4 4.7a1.2 1.2 0 00-1.7-.1l-6.6 6.5a1.2 1.2 0 000 1.8l6.6 6.5a1.2 1.2 0 001.7-1.7L9.7 12l5.7-5.7a1.2 1.2 0 000-1.6z" fill="currentColor" />
  </Svg>
);

export const IconChevronRight: React.FC<IconProps> = ({ className = 'h-3.5 w-3.5' }) => (
  <Svg className={className}>
    <path d="M8.6 4.7a1.2 1.2 0 011.7-.1l6.6 6.5a1.2 1.2 0 010 1.8l-6.6 6.5a1.2 1.2 0 01-1.7-1.7l5.7-5.7-5.7-5.7a1.2 1.2 0 010-1.6z" fill="currentColor" />
  </Svg>
);

/**
 * `draw` lands the mark by drawing it, the one stroke in the kit: a line that draws itself is a
 * stroke by nature. Without `draw` it is a filled shape like everything else.
 */
export const IconCheck: React.FC<IconProps & { draw?: boolean }> = ({ className = 'h-3.5 w-3.5', draw }) => (
  <Svg className={className}>
    {draw ? (
      <path
        d="M5 12.5l4.5 4.5L19 7.5"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={24}
        className="motion-safe:animate-checkDraw"
      />
    ) : (
      <path d="M19.8 6.4a1.2 1.2 0 01.1 1.7l-9.8 10.3a1.2 1.2 0 01-1.7 0l-4.3-4.4a1.2 1.2 0 011.7-1.7l3.4 3.5 9-9.4a1.2 1.2 0 011.6 0z" fill="currentColor" />
    )}
  </Svg>
);

export const IconClose: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <rect x="10.8" y="3.5" width="2.4" height="17" rx="1.2" transform="rotate(45 12 12)" fill="currentColor" />
    <rect x="10.8" y="3.5" width="2.4" height="17" rx="1.2" transform="rotate(-45 12 12)" fill="currentColor" />
  </Svg>
);

export const IconPlus: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <rect x="10.8" y="4" width="2.4" height="16" rx="1.2" fill="currentColor" />
    <rect x="4" y="10.8" width="16" height="2.4" rx="1.2" fill="currentColor" />
  </Svg>
);

/** Peek: look at a thing without leaving where you are. */
export const IconEye: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path
      fillRule="evenodd"
      d="M12 5C7 5 3.1 8.1 1.5 12c1.6 3.9 5.5 7 10.5 7s8.9-3.1 10.5-7C20.9 8.1 17 5 12 5zm0 2c3.7 0 6.8 2.1 8.3 5-1.5 2.9-4.6 5-8.3 5s-6.8-2.1-8.3-5C5.2 9.1 8.3 7 12 7z"
      fill="currentColor"
    />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </Svg>
);

export const IconClock: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path fillRule="evenodd" d="M12 3a9 9 0 100 18 9 9 0 000-18zm0 2a7 7 0 110 14 7 7 0 010-14z" fill="currentColor" />
    <path d="M11 7.5a1 1 0 012 0v4.1l2.7 2.7a1 1 0 01-1.4 1.4l-3-3a1 1 0 01-.3-.7V7.5z" fill="currentColor" />
  </Svg>
);

export const IconSearch: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path
      fillRule="evenodd"
      d="M10.5 3a7.5 7.5 0 104.55 13.46l4.24 4.25a1.1 1.1 0 001.56-1.56l-4.25-4.24A7.5 7.5 0 0010.5 3zm0 2.2a5.3 5.3 0 110 10.6 5.3 5.3 0 010-10.6z"
      fill="currentColor"
    />
  </Svg>
);

export const IconCalendar: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path
      fillRule="evenodd"
      d="M7 2.5a1 1 0 011 1V5h8V3.5a1 1 0 112 0V5h.5A2.5 2.5 0 0121 7.5v11a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 18.5v-11A2.5 2.5 0 015.5 5H6V3.5a1 1 0 011-1zM5 10v8.5a.5.5 0 00.5.5h13a.5.5 0 00.5-.5V10H5z"
      fill="currentColor"
    />
  </Svg>
);

/** Opens somewhere else: a linked record, another page. */
export const IconArrowUpRight: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path d="M8 5.5a1 1 0 011-1h9.5a1 1 0 011 1V15a1 1 0 11-2 0V7.9l-10.8 10.8a1 1 0 01-1.4-1.4L16.1 6.5H9a1 1 0 01-1-1z" fill="currentColor" />
  </Svg>
);

export const IconPencil: React.FC<IconProps> = ({ className = 'h-3.5 w-3.5' }) => (
  <Svg className={className}>
    <path d="M15.2 4.4a2 2 0 012.8 0l1.6 1.6a2 2 0 010 2.8L9 19.4l-4.6 1.1a.8.8 0 01-1-1L4.6 15 15.2 4.4z" fill="currentColor" />
  </Svg>
);

/** Something needs a look before it goes further. Pair it with words; the shape is not the message. */
export const IconWarning: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path
      fillRule="evenodd"
      d="M10.89 4.7Q12 2.8 13.11 4.7L21.39 18.87Q22.4 20.6 20.4 20.6H3.6Q1.6 20.6 2.61 18.87L10.89 4.7zM12 8.6a1.1 1.1 0 011.1 1.1v3.6a1.1 1.1 0 01-2.2 0V9.7A1.1 1.1 0 0112 8.6zm0 7.35a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5z"
      fill="currentColor"
    />
  </Svg>
);

/**
 * The firefly mark for empty states: a four-point glint with a smaller one beside it. Pass a
 * gold text class to make it the subject; it is never decoration on a control.
 */
export const IconSpark: React.FC<IconProps> = ({ className = 'h-8 w-8' }) => (
  <Svg className={className}>
    <path d="M11 3c.8 5 3.5 7.7 8.5 8.5-5 .8-7.7 3.5-8.5 8.5-.8-5-3.5-7.7-8.5-8.5 5-.8 7.7-3.5 8.5-8.5z" fill="currentColor" />
    <path d="M19 2.5c.2 1.3.9 2 2.2 2.2-1.3.2-2 .9-2.2 2.2-.2-1.3-.9-2-2.2-2.2 1.3-.2 2-.9 2.2-2.2z" fill="currentColor" opacity={0.6} />
  </Svg>
);

/** Something failed: a disc with the mark knocked out. The warning is a triangle, so the two differ by shape. */
export const IconError: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path
      fillRule="evenodd"
      d="M12 2.5a9.5 9.5 0 100 19 9.5 9.5 0 000-19zM12 6.9a1.1 1.1 0 011.1 1.1v4.6a1.1 1.1 0 01-2.2 0V8A1.1 1.1 0 0112 6.9zm0 8.15a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5z"
      fill="currentColor"
    />
  </Svg>
);

/** Worth knowing, nothing wrong. */
export const IconInfo: React.FC<IconProps> = ({ className = 'h-4 w-4' }) => (
  <Svg className={className}>
    <path
      fillRule="evenodd"
      d="M12 2.5a9.5 9.5 0 100 19 9.5 9.5 0 000-19zm0 4a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM12 10.3a1.1 1.1 0 011.1 1.1v4.6a1.1 1.1 0 01-2.2 0v-4.6a1.1 1.1 0 011.1-1.1z"
      fill="currentColor"
    />
  </Svg>
);

/** A turn of the arrow: load again. */
export const IconRefresh: React.FC<IconProps> = ({ className = 'h-5 w-5' }) => (
  <Svg className={className}>
    <path d="M19.61 9.23A8.1 8.1 0 1 1 12 3.9V6.1A5.9 5.9 0 1 0 17.54 9.98Z" fill="currentColor" />
    <path d="M12 1.6L16.2 5 12 8.4Z" fill="currentColor" />
  </Svg>
);

/** A step on hold. */
export const IconPause: React.FC<IconProps> = ({ className = 'h-3.5 w-3.5' }) => (
  <Svg className={className}>
    <rect x="6.5" y="5" width="4" height="14" rx="1.2" fill="currentColor" />
    <rect x="13.5" y="5" width="4" height="14" rx="1.2" fill="currentColor" />
  </Svg>
);
